import React, { useRef, useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { FileUpload, FileUploadSelectEvent } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import {
  FirmwareMetadata,
  EspFirmwareMetadata,
  validateVersionMatch,
  getVersionFromMetadata,
  isCm7Cm4Metadata,
  isEspMetadata,
} from '@marsinet/commons';

import { apiServices } from '../../../../services/apiServices';
import { configureAxios } from '../../../../utils/utils';
import { MarsinetContext } from '../../../../context/marsinetProvider';
import {
  calculateFileChecksum,
  formatVersionMismatchWarning,
  isValidUploadToken,
  isValidVersion,
  parseFileMetadata,
} from '../utils';

interface SoftwareTypeOption {
  label: string;
  value: number;
}

export const OtaUploadForm: React.FC = () => {
  const { t } = useTranslation();
  const { access_token } = useContext(MarsinetContext);
  const toastRef = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedSoftwareType, setSelectedSoftwareType] = useState<
    number | null
  >(null);
  const [version, setVersion] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [fileChecksum, setFileChecksum] = useState<string | null>(null);
  const [fileMetadata, setFileMetadata] = useState<
    FirmwareMetadata | EspFirmwareMetadata | null
  >(null);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [versionMismatchWarning, setVersionMismatchWarning] = useState<
    string | null
  >(null);
  const [versionFormatError, setVersionFormatError] = useState<string | null>(
    null
  );
  const [uploadToken, setUploadToken] = useState<string | null>(null);
  const [tokenValid, setTokenValid] = useState<boolean>(false);
  const [requestingToken, setRequestingToken] = useState(false);

  const softwareTypeOptions: SoftwareTypeOption[] = [
    {
      label: 'CM7',
      value: 0,
    },
    {
      label: 'CM4',
      value: 1,
    },
    {
      label: 'ESP',
      value: 2,
    },
  ];

  const handleFileSelect = (event: FileUploadSelectEvent) => {
    if (event.files && event.files.length > 0) {
      const file = event.files[0];
      setSelectedFile(file);
      setMetadataError(null);
      setVersionMismatchWarning(null);
      setVersionFormatError(null);
      calculateFileChecksum(file, setFileChecksum);
      parseFileMetadata(
        file,
        setFileMetadata,
        setMetadataError,
        setVersionMismatchWarning,
        version,
        toastRef,
        t
      );
    }
  };

  const handleUploadTokenChange = (newToken: string) => {
    setUploadToken(newToken);

    if (newToken.trim()) {
      if (isValidUploadToken(newToken)) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
      }
    } else {
      setTokenValid(false);
    }
  };

  const handleRequestToken = async () => {
    if (selectedSoftwareType === null) {
      toastRef.current?.show({
        severity: 'warn',
        summary: t('messages.warning'),
        detail: t('pages.maintenance.ota.select_software_type'),
        life: 3000,
      });
      return;
    }

    setRequestingToken(true);
    try {
      configureAxios(access_token);

      await apiServices.otaApi.otaControllerGenerateUploadToken({
        software_type: selectedSoftwareType,
      });

      toastRef.current?.show({
        severity: 'success',
        summary: t('messages.success'),
        detail: t('pages.maintenance.ota.token_request_sent'),
        life: 5000,
      });

      setRequestingToken(false);
    } catch (error: any) {
      toastRef.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail:
          error.response?.data?.message ||
          t('pages.maintenance.ota.error_requesting_token'),
        life: 3000,
      });
      setRequestingToken(false);
    }
  };

  const handleVersionChange = (newVersion: string) => {
    setVersion(newVersion);

    // Validar formato de versión mientras se escribe
    if (newVersion.trim()) {
      if (!isValidVersion(newVersion)) {
        setVersionFormatError(
          `${t('pages.maintenance.ota.version_format_invalid')} - ${t('pages.maintenance.ota.version_placeholder')}`
        );
      } else {
        setVersionFormatError(null);
      }
    } else {
      setVersionFormatError(null);
    }

    // Validar contra metadatos si existen
    if (fileMetadata && newVersion.trim() && isValidVersion(newVersion)) {
      const metadataVersion = getVersionFromMetadata(fileMetadata);
      const validationResult = validateVersionMatch(
        newVersion,
        metadataVersion
      );
      console.log('validation result: ', validationResult);
      if (!validationResult.match) {
        setVersionMismatchWarning(
          formatVersionMismatchWarning(
            validationResult.message ||
              t('pages.maintenance.ota.version_mismatch_metadata')
          )
        );
      } else {
        setVersionMismatchWarning(null);
      }
    } else {
      setVersionMismatchWarning(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || selectedSoftwareType === null) {
      toastRef.current?.show({
        severity: 'warn',
        summary: t('messages.warning'),
        detail: t('pages.maintenance.ota.select_file_and_type'),
        life: 3000,
      });
      return;
    }

    if (metadataError) {
      toastRef.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('pages.maintenance.ota.invalid_metadata') + metadataError,
        life: 3000,
      });
      return;
    }

    if (!version.trim()) {
      toastRef.current?.show({
        severity: 'warn',
        summary: t('messages.warning'),
        detail: t('pages.maintenance.ota.version_required'),
        life: 3000,
      });
      return;
    }

    if (!isValidVersion(version)) {
      toastRef.current?.show({
        severity: 'warn',
        summary: t('messages.warning'),
        detail: t('pages.maintenance.ota.version_format_invalid'),
        life: 3000,
      });
      return;
    }

    if (versionMismatchWarning) {
      toastRef.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: versionMismatchWarning,
        life: 4000,
      });
      return;
    }

    // ✅ Validar token de autorización (requerido)
    if (!uploadToken || !uploadToken.trim()) {
      toastRef.current?.show({
        severity: 'warn',
        summary: t('messages.warning'),
        detail: t('pages.maintenance.ota.token_required'),
        life: 3000,
      });
      return;
    }

    if (!tokenValid) {
      toastRef.current?.show({
        severity: 'warn',
        summary: t('messages.warning'),
        detail: t('pages.maintenance.ota.token_format_invalid'),
        life: 3000,
      });
      return;
    }

    if (!access_token) {
      toastRef.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('messages.authentication_required'),
        life: 3000,
      });
      return;
    }

    configureAxios(access_token);
    setLoading(true);

    try {
      const response = await apiServices.otaApi.otaControllerUploadFirmwareForm(
        selectedFile,
        selectedSoftwareType,
        version.trim(),
        fileChecksum,
        JSON.stringify(fileMetadata),
        uploadToken
      );

      toastRef.current?.show({
        severity: 'success',
        summary: t('messages.success'),
        detail: `${response.data.message} - ${t('pages.maintenance.ota.version')}: ${response.data.version}`,
        life: 5000,
      });

      // Reset form
      setSelectedFile(null);
      setSelectedSoftwareType(null);
      setVersion('');
      setFileChecksum(null);
      setFileMetadata(null);
      setMetadataError(null);
      setVersionMismatchWarning(null);
      setVersionFormatError(null);
      setUploadToken(null);
      setTokenValid(false);
      fileUploadRef.current?.clear();
    } catch (error: any) {
      let errorMessage = t('messages.error_uploading_firmware');

      if (error.response?.status === 400) {
        if (
          error.response.data?.message?.includes('File integrity check failed')
        ) {
          errorMessage =
            t('pages.maintenance.ota.checksum_mismatch') ||
            'File integrity check failed. The file may have been corrupted during upload.';
        } else if (error.response.data?.message?.includes('metadata')) {
          errorMessage = error.response.data.message;
        } else if (
          error.response.data?.message?.includes('token') ||
          error.response.data?.message?.includes('authorization')
        ) {
          if (error.response.data?.message?.includes('already been used')) {
            errorMessage = t('pages.maintenance.ota.token_already_used');
          } else {
            errorMessage =
              'Upload authorization failed: ' + error.response.data.message;
          }
        } else {
          errorMessage =
            error.response.data?.message ||
            t('pages.maintenance.ota.invalid_software_type');
        }
      } else if (error.response?.status === 409) {
        errorMessage =
          error.response.data?.message ||
          t('pages.maintenance.ota.version_conflict');
      }

      toastRef.current?.show({
        severity: 'error',
        summary: t('messages.error'),
        detail: errorMessage,
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    selectedFile &&
    selectedSoftwareType !== null &&
    isValidVersion(version) &&
    !versionMismatchWarning &&
    !versionFormatError;

  return (
    <>
      <Toast ref={toastRef} />
      <Card className="p-4">
        <div className="grid formgrid p-fluid">
          <div className="col-12 mb-4">
            <h1>{t('pages.maintenance.ota.title')}</h1>
            <p className="text-color-secondary">
              {t('pages.maintenance.ota.upload_description')}
            </p>
          </div>

          {/* Software Type Selection */}
          <div className="field mb-3 col-12 md:col-6">
            <label className="font-bold block mb-2">
              {t('pages.maintenance.ota.software_type')}*
            </label>
            <Dropdown
              value={selectedSoftwareType}
              onChange={(e: DropdownChangeEvent) =>
                setSelectedSoftwareType(e.target.value)
              }
              options={softwareTypeOptions}
              optionLabel="label"
              optionValue="value"
              placeholder={t('pages.maintenance.ota.select_software_type')}
              className="w-full"
            />
          </div>

          {/* Version Input */}
          <div className="field mb-3 col-12 md:col-6">
            <label className="font-bold block mb-2">
              {t('pages.maintenance.ota.version_required')}
            </label>
            <input
              value={version}
              onChange={(e) => handleVersionChange(e.target.value)}
              placeholder={t('pages.maintenance.ota.version_placeholder')}
              disabled={loading}
              className={`p-inputtext p-component ${versionFormatError ? 'ng-invalid ng-touched' : ''}`}
              autoComplete="new-password"
              spellCheck="false"
              type="password"
              name="fw_version_pwd"
              style={{ fontFamily: 'monospace' }}
              onFocus={(e) => {
                e.currentTarget.type = 'text';
              }}
              onBlur={(e) => {
                if (!e.currentTarget.value) {
                  e.currentTarget.type = 'password';
                }
              }}
            />
            <small className="text-color-secondary block mt-1">
              {t('pages.maintenance.ota.version_info')}
            </small>
            {versionFormatError && (
              <small className="text-red-600 block mt-1">
                ❌ {versionFormatError}
              </small>
            )}
            {fileMetadata && version && isValidVersion(version) && (
              <small className="text-color-secondary block mt-1">
                {t('pages.maintenance.ota.file_metadata_version')}:{' '}
                <strong>{getVersionFromMetadata(fileMetadata)}</strong>
              </small>
            )}
            {versionMismatchWarning && (
              <small className="text-orange-600 block mt-1">
                ⚠️ {versionMismatchWarning}
              </small>
            )}
          </div>

          {/* File Upload */}
          <div className="field mb-3 col-12">
            <label className="font-bold block mb-2">
              {t('pages.maintenance.ota.firmware_file')}
            </label>
            <FileUpload
              ref={fileUploadRef}
              name="file"
              accept=".bin,.hex,.elf"
              maxFileSize={500000000}
              onSelect={handleFileSelect}
              chooseLabel={t('pages.maintenance.ota.choose_file')}
              customUpload
              disabled={loading}
              auto={false}
            />
            <style>{`
              .p-fileupload .p-button {
                display: none !important;
              }
              .p-fileupload .p-button:first-child {
                display: inline-flex !important;
              }
              .p-fileupload .p-badge {
                display: none !important;
              }
            `}</style>
            {selectedFile && (
              <small className="text-color-secondary block mt-2">
                {t('pages.maintenance.ota.selected_file')}: {selectedFile.name}{' '}
                ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </small>
            )}
          </div>

          {/* Metadata Display */}
          {metadataError && (
            <div className="col-12 mb-3">
              <Message severity="error" text={metadataError} />
            </div>
          )}

          {fileMetadata && !metadataError && (
            <div className="col-12 mb-3">
              <Card className="p-3" style={{ backgroundColor: '#f8f9fa' }}>
                <h4 className="mb-3">
                  {t('pages.maintenance.ota.firmware_metadata')}
                </h4>
                <div className="grid">
                  {isCm7Cm4Metadata(fileMetadata) && (
                    <>
                      <div className="col-12 md:col-6">
                        <div className="mb-2">
                          <label className="font-bold">
                            {t('pages.maintenance.ota.project')}:
                          </label>
                          <p>{fileMetadata.projectName}</p>
                        </div>
                        <div className="mb-2">
                          <label className="font-bold">
                            {t('pages.maintenance.ota.binary_version')}:
                          </label>
                          <p>{fileMetadata.binaryVersion}</p>
                        </div>
                      </div>
                      <div className="col-12 md:col-6">
                        <div className="mb-2">
                          <label className="font-bold">
                            {t('pages.maintenance.ota.board_version')}:
                          </label>
                          <p>{fileMetadata.boardVersion}</p>
                        </div>
                        <div className="mb-2">
                          <label className="font-bold">
                            {t('pages.maintenance.ota.microcontroller_id')}:
                          </label>
                          <p>{fileMetadata.microcontrollerID}</p>
                        </div>
                      </div>
                      {fileMetadata.creationDateTime && (
                        <div className="col-12">
                          <div className="mb-2">
                            <label className="font-bold">
                              {t('pages.maintenance.ota.creation_datetime')}:
                            </label>
                            <p>{fileMetadata.creationDateTime}</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {isEspMetadata(fileMetadata) && (
                    <>
                      <div className="col-12 md:col-6">
                        <div className="mb-2">
                          <label className="font-bold">
                            {t('pages.maintenance.ota.project')}:
                          </label>
                          <p>{fileMetadata.projectName}</p>
                        </div>
                        <div className="mb-2">
                          <label className="font-bold">
                            {t('pages.maintenance.ota.git_tag')}:
                          </label>
                          <p>{fileMetadata.gitTag}</p>
                        </div>
                      </div>
                      <div className="col-12 md:col-6">
                        <div className="mb-2">
                          <label className="font-bold">
                            {t('pages.maintenance.ota.chip_id')}:
                          </label>
                          <p>{fileMetadata.chipID}</p>
                        </div>
                        <div className="mb-2">
                          <label className="font-bold">
                            {t('pages.maintenance.ota.compilation_date')}:
                          </label>
                          <p>{fileMetadata.compilationDate}</p>
                        </div>
                      </div>
                      {fileMetadata.compilationTime && (
                        <div className="col-12">
                          <div className="mb-2">
                            <label className="font-bold">
                              {t('pages.maintenance.ota.compilation_time')}:
                            </label>
                            <p>{fileMetadata.compilationTime}</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* Request Token Section */}
          {selectedSoftwareType !== null && selectedFile && !metadataError && (
            <div className="col-12 border-top-1 border-300 pt-4 mb-4">
              <h3 className="mb-3">
                {t('pages.maintenance.ota.dont_have_token')}
              </h3>
              <Button
                label={
                  requestingToken
                    ? t('messages.loading')
                    : t('pages.maintenance.ota.request_token_button')
                }
                onClick={handleRequestToken}
                loading={requestingToken}
                icon={requestingToken ? '' : 'pi pi-send'}
                severity="info"
              />
            </div>
          )}

          {/* Upload Authorization Token */}
          {selectedSoftwareType !== null &&
            version.trim() !== '' &&
            selectedFile && (
              <div className="field mb-3 col-12">
                <label className="font-bold block mb-2">
                  {t('pages.maintenance.ota.upload_authorization')}*
                </label>
                <InputText
                  value={uploadToken || ''}
                  onChange={(e) => handleUploadTokenChange(e.target.value)}
                  placeholder="ota_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  disabled={loading}
                  type="password"
                  className={`flex-1 ${uploadToken && !tokenValid ? 'ng-invalid ng-touched' : ''}`}
                />
                <small className="text-color-secondary block mt-1">
                  {t('pages.maintenance.ota.upload_authorization_info')}
                </small>
                {uploadToken && tokenValid && (
                  <small className="text-green-600 block mt-1">
                    {t('pages.maintenance.ota.token_valid')}
                  </small>
                )}
                {uploadToken && !tokenValid && (
                  <small className="text-red-600 block mt-1">
                    {t('pages.maintenance.ota.token_invalid_format')}
                  </small>
                )}
              </div>
            )}
          {/* Upload Button */}
          <div className="col-12 flex gap-2 justify-content-end">
            <Button
              label={
                loading
                  ? t('pages.maintenance.ota.uploading')
                  : t('pages.maintenance.ota.upload')
              }
              icon={loading ? '' : 'pi pi-upload'}
              onClick={handleUpload}
              disabled={!isFormValid || loading}
              loading={loading}
            />
          </div>

          {loading && (
            <div className="col-12 flex justify-content-center mt-4">
              <ProgressSpinner style={{ width: '50px', height: '50px' }} />
            </div>
          )}
        </div>
      </Card>
    </>
  );
};

export default OtaUploadForm;
